import type { TCountry } from '../../../types/common';
import type {
  IFiltersBar,
  TFieldValidation,
  TInputFilter,
} from '../../../types/data_table';

import { ALL_COUNTRIES_INFO } from '../../../constants/all-countries-info';
import { COUNTRIES_INFO } from '../../../constants/countries';

import { DropdownMenu } from '../../Dropdown/DropdownMenu';

/** Country the phone field falls back to when the consumer names none. */
export const DEFAULT_COUNTRY = 'EGY';

export interface IFieldFilterMenu {
  filter: TInputFilter;
  lang: IFiltersBar['lang'];
  isMobile?: boolean;
  /** Country whose dial code and format the phone field uses. */
  country: TCountry;
  localization: IFiltersBar['localization'];
  /** Validation message currently shown under the field, if any. */
  errorMsg?: string;
  /** Called on every keystroke with the value to validate. */
  onValidate: (
    value: string,
    id: string,
    validations?: TFieldValidation[]
  ) => void;
  /**
   * Called with the newly picked country and the validations that now apply,
   * so whatever is already typed can be checked against them.
   */
  onCountryChange: (
    country: TCountry,
    validations?: TFieldValidation[]
  ) => void;
  /** Called with the entered value when the user confirms. */
  onApply: (value: string) => void;
}

/**
 * Everything the phone field needs to know about one country.
 *
 * The field offers every country in the world, but only the ones Paymob
 * operates in carry a validation pattern and a placeholder — those live in
 * `COUNTRIES_INFO`, keyed by ISO-3. `ALL_COUNTRIES_INFO` is keyed by ISO-2 and
 * covers the rest, with no pattern to validate against.
 */
const getCountryInfo = (countryIso3: TCountry) => {
  const paymobCountry = COUNTRIES_INFO[countryIso3 || DEFAULT_COUNTRY];

  if (paymobCountry) {
    return {
      iso2: paymobCountry.country_code_iso2.toLowerCase(),
      placeholder: paymobCountry.placeholder,
      phonePattern: paymobCountry.phone_regex?.source?.toString() || '',
    };
  }

  const anyCountry = Object.values(ALL_COUNTRIES_INFO).find(
    (item) => item.countryIso3 === countryIso3
  );

  return {
    iso2: anyCountry?.countryIso2,
    placeholder: undefined,
    // No pattern: an empty one matches anything, so numbers from countries
    // Paymob has no rule for are accepted as typed.
    phonePattern: '',
  };
};

/**
 * The pop-up that a text or phone filter is edited in.
 *
 * This is a render *function* rather than a component on purpose: `Dropdown`
 * finds its menu by matching `child.type === DropdownMenu`, so wrapping the
 * menu in a component of our own would leave it unable to open.
 */
export const renderFieldFilterMenu = ({
  filter,
  lang,
  isMobile,
  country,
  localization,
  errorMsg,
  onValidate,
  onCountryChange,
  onApply,
}: IFieldFilterMenu) => {
  const { id, type, validations, placeholder } = filter;

  /** Shared between both field types. */
  const commonProps = {
    lang,
    width: '15rem',
    fieldName: id,
    horizontalPlacement: (isMobile ? 'center' : 'inline_auto') as
      | 'center'
      | 'inline_auto',
    errorMsg,
    applyBtnText: localization?.apply,
    onApplyHandler: onApply,
  };

  if (type === 'text') {
    return (
      <DropdownMenu
        {...commonProps}
        type="text_field"
        placeholder={placeholder}
        verticalPlacement="auto"
        onChangeHandler={(e) => onValidate(e.target.value, id, validations)}
      />
    );
  }

  /**
   * The server's phone rules are per-merchant, not per-country, so the message
   * is kept and only the pattern is swapped for the picked country's.
   */
  const withCountryPattern = (pattern: string) =>
    validations?.map((validation) => ({ ...validation, pattern }));

  const countryInfo = getCountryInfo(country);

  return (
    <DropdownMenu
      {...commonProps}
      type="phone_field"
      maxHeight="fit-content"
      placeholder={placeholder || countryInfo.placeholder}
      verticalPlacement="auto"
      country={countryInfo.iso2}
      showAllCountries
      onChangeHandler={(_, __, phoneWithoutCountryCode) =>
        onValidate(
          phoneWithoutCountryCode || '',
          id,
          withCountryPattern(countryInfo.phonePattern)
        )
      }
      // The field reports ISO-2; the rest of the bar speaks ISO-3.
      onCountryChange={(picked) => {
        const nextCountryIso3 = ALL_COUNTRIES_INFO[picked]
          ?.countryIso3 as TCountry;

        onCountryChange(
          nextCountryIso3,
          withCountryPattern(getCountryInfo(nextCountryIso3).phonePattern)
        );
      }}
    />
  );
};
